import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Search, Sparkles, UsersRound, X } from "lucide-react";
import { AppDispatch, RootState } from "../../../../store";
import { getInfinityCommission } from "../../../../Redux/referral/slice";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import PrtCommissionTable from "./PrtCommissionTable";
import {
  formatEarningsCurrency,
  mapInfinityItemToSummary,
} from "../earningsAdapters";
import "./SamriddhiIncentives.css";

const PrtCommisson = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { infinityCommission } = useSelector(
    (state: RootState) => state.referral,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (dateRange.from && !dateRange.to) return;

    dispatch(
      getInfinityCommission({
        page,
        limit,
        status: "ALL",
        sortOrder: "DESC",
        search: debouncedSearch || undefined,
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      }),
    );
  }, [dispatch, page, limit, debouncedSearch, dateRange.from, dateRange.to]);

  const records = (infinityCommission.data || []).map(
    mapInfinityItemToSummary,
  );
  const totalRecords =
    infinityCommission.pagination?.total ?? records.length;

  return (
    <section className="samriddhi-page">
      <div className="samriddhi-shell">
        <header className="samriddhi-page-header">
          <div className="samriddhi-title-wrap">
            <span className="samriddhi-title-icon">
              <Sparkles size={23} />
            </span>
            <div>
              <span className="samriddhi-eyebrow">MY REWARDS</span>
              <h1>Salar Samriddhi Incentives</h1>
              <p>
                Track monthly contributor activity and the Samriddhi
                incentives received.
              </p>
            </div>
          </div>
          <span className="samriddhi-record-count">
            {totalRecords} records
          </span>
        </header>

        <div className="samriddhi-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Entries per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option value={val} key={val}>
                {val} Entries
              </option>
            ))}
          </select>

          <div className="samriddhi-date-picker">
            <DateRangePicker
              value={dateRange}
              onChange={(v) => {
                setDateRange(v);
                setPage(1);
              }}
            />
          </div>

          <div className="samriddhi-search">
            <Search size={17} />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search months or years..."
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        <PrtCommissionTable data={infinityCommission.data || []} />

        <div className="samriddhi-mobile-list">
          {records.map((record, index) => (
            <article className="samriddhi-mobile-card" key={record.id}>
              <div className="samriddhi-mobile-card-top">
                <div>
                  <span className="samriddhi-mobile-label">PERIOD</span>
                  <h2>
                    {record.monthYearLabel ||
                      `${new Date(record.year, record.month - 1).toLocaleString("default", { month: "long" })} - ${record.year}`}
                  </h2>
                </div>
                <span className="samriddhi-mobile-index">#{index + 1}</span>
              </div>
              <div className="samriddhi-mobile-metrics">
                <Link
                  className="samriddhi-mobile-contributors"
                  to={`/PrtCommissonDetails?date=${record.monthYear}`}
                >
                  <span>Contributors</span>
                  <strong>{record.totalPurchases}</strong>
                  <small>Tap to view details</small>
                </Link>
                <div>
                  <span>Received Incentives</span>
                  <strong>
                    {formatEarningsCurrency(record.totalCommissionEarned)}
                  </strong>
                </div>
              </div>
              <Link
                className="samriddhi-mobile-view-button"
                to={`/PrtCommissonDetails?date=${record.monthYear}`}
              >
                <UsersRound size={17} />
                View Contributor Details
              </Link>
            </article>
          ))}
        </div>

        <Pagenation
          currentPage={page}
          totalPages={infinityCommission.pagination?.totalPages || 0}
          onPageChange={setPage}
        />
      </div>
    </section>
  );
};

export default PrtCommisson;
